import { IFilterGroup } from './map-odata-filters';
import { ISort } from './sort';

export interface ITableQueryParams<T> {
  page: number;
  size: number;
  sort?: ISort<T>;
  filters?: IFilterGroup;
}

/** URL param names are prefixed, so two tables can share one page. */
export const tableQueryParamNames = (prefix: string) => ({
  filters: `${prefix}_filters`,
  page: `${prefix}_page`,
  size: `${prefix}_size`,
  sort: `${prefix}_sort`,
});

/** Falls back rather than throwing: these values come from a user-editable URL. */
export const parsePositiveIntParam = (value: string, fallback: number): number => {
  const parsed = Number(value);

  return Number.isNaN(parsed) || parsed <= 0 ? fallback : parsed;
};

/** Direction is an arrow prefix on the key: `key`, `⬆key`, `⬇key`. */
export const parseSortParam = <T>(sortParam: string | undefined): ISort<T> | undefined => {
  if (!sortParam) {
    return undefined;
  }

  const matched = /([⬆⬇]?)(.+)/u.exec(sortParam);

  if (!matched) {
    return undefined;
  }

  return {
    direction: matched[1] === '⬇' ? 'desc' : 'asc',
    key: matched[2] as keyof T,
  };
};

export const stringifySortParam = <T>({ key, direction }: ISort<T>): string =>
  `${direction === 'asc' ? '⬆' : '⬇'}${String(key)}`;

/**
 * Fails closed. The param is user-editable, so a hand-mangled URL would otherwise throw
 * out of the query-param stream and take the table's pagination down with it — and the
 * result is still not *guaranteed* to be an IFilterGroup, only valid JSON.
 */
export const parseFiltersParam = (filtersParam: string | undefined): IFilterGroup | undefined => {
  if (!filtersParam) {
    return undefined;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return JSON.parse(decodeURIComponent(filtersParam));
  } catch {
    return undefined;
  }
};

export const stringifyFiltersParam = (filters: IFilterGroup): string | undefined =>
  filters.filters.length === 0 ? undefined : encodeURIComponent(JSON.stringify(filters));
